import { openDB } from "idb";
import { seal, sealJSON, unseal, unsealJSON } from "./crypto";

const dbp = openDB("starvault", 1, {
  upgrade(db) {
    db.createObjectStore("folders", { keyPath: "id" });
    db.createObjectStore("notes", { keyPath: "id" }).createIndex("folderId", "folderId");
    db.createObjectStore("versions", { keyPath: "id" }).createIndex("noteId", "noteId");
    db.createObjectStore("files", { keyPath: "id" }).createIndex("noteId", "noteId");
    db.createObjectStore("kv");
  },
});

// Only ids/index keys stay readable; everything else is AES-GCM encrypted with the vault key
const INDEX = { folders: [], notes: ["folderId"], versions: ["noteId"], files: ["noteId"] };
const KV_PLAIN = ["security", "bioKey"];

async function wrap(s, v, k) {
  if (s === "kv") return KV_PLAIN.includes(k) ? v : { _enc: await sealJSON(v) };
  const { blob, ...rest } = v;
  const out = { id: v.id, _enc: await sealJSON(rest) };
  INDEX[s].forEach((f) => { out[f] = v[f]; });
  if (blob) { out._blob = await seal(await blob.arrayBuffer()); out._mime = blob.type; }
  return out;
}

async function unwrap(r) {
  if (!r || !r._enc) return r;
  const o = await unsealJSON(r._enc);
  if (r._blob) o.blob = new Blob([await unseal(r._blob)], { type: r._mime });
  return o;
}

export const db = {
  all: async (s) => Promise.all((await (await dbp).getAll(s)).map(unwrap)),
  byIndex: async (s, idx, val) => Promise.all((await (await dbp).getAllFromIndex(s, idx, val)).map(unwrap)),
  get: async (s, k) => unwrap(await (await dbp).get(s, k)),
  put: async (s, v, k) => (await dbp).put(s, await wrap(s, v, k), k),
  del: async (s, k) => (await dbp).delete(s, k),
  // add() rejects if the key exists, so versions can never be overwritten
  addVersion: async (v) => (await dbp).add("versions", await wrap("versions", v)),
};

export const kv = {
  get: (k) => db.get("kv", k),
  set: (k, v) => db.put("kv", v, k),
  del: (k) => db.del("kv", k),
};

// One-time upgrade of any plaintext records left from older versions of the app
export async function encryptAll() {
  const d = await dbp;
  for (const s of Object.keys(INDEX)) {
    for (const r of await d.getAll(s)) if (!r._enc) await d.put(s, await wrap(s, r));
  }
  for (const k of await d.getAllKeys("kv")) {
    const v = await d.get("kv", k);
    if (!KV_PLAIN.includes(k) && v && !v._enc) await d.put("kv", await wrap("kv", v, k), k);
  }
}

export const BUILTIN_FOLDERS = [
  { id: "appointments", name: "Appointments & Schedule", icon: "clock" },
  { id: "daily-journal", name: "Daily Journal", icon: "book" },
  { id: "health", name: "Health, Medical & Wellness", icon: "heart" },
  { id: "receipts-tracking", name: "Receipts & Invoices", icon: "receipt" },
  { id: "proof-of-service", name: "Proof of Service & Delivery", icon: "truck" },
  { id: "vehicle-property", name: "Vehicle & Property Inspection", icon: "car" },
  { id: "dispute-legal-log", name: "Dispute & Legal Log", icon: "scale" },
];

export async function ensureFolders() {
  for (const [i, f] of BUILTIN_FOLDERS.entries()) {
    const cur = await db.get("folders", f.id);
    if (!cur) await db.put("folders", { ...f, builtin: true, order: i, createdAt: new Date().toISOString() });
    else if (cur.name !== f.name || cur.order !== i || cur.icon !== f.icon) await db.put("folders", { ...cur, name: f.name, order: i, icon: f.icon });
  }
}
